import React, { useState } from 'react';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import { CardMedia } from '@mui/material';
import { Button, IconButton, Select, TextInput } from 'components';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { TPhoneCaseForm, TPhoneCaseFormValues } from './PhoneCaseForm.types';
import { useAppSelector } from 'store/store';
import CustomFieldsOptions from './components/CustomFieldsOptions/CustomFieldsOptions';

const PhoneCaseForm: TPhoneCaseForm = ({
  onSubmit,
  isLoading = false,
  defaultImageLinks = [],
}) => {
  const {
    tagsList: { data: tagsList },
  } = useAppSelector((store) => store.tags);

  const {
    getValues,
    watch,
    handleSubmit,
    control,
    formState: { errors },
  } = useFormContext<TPhoneCaseFormValues>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'customFields',
  });

  const [images, setImages] = useState<Array<File | string>>(defaultImageLinks);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const selected = Array.from(e.target.files);
    const updated = [...images, ...selected].slice(0, 5);

    setImages(updated);
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const addCustomField = () => {
    append({
      name: '',
      type: 'string',
      placeholder: '',
      maxLength: 30,
      minLength: 3,
    });
  };

  const onFormSubmit = (data: TPhoneCaseFormValues) => {
    onSubmit(data, images);
  };

  return (
    <div className="admin-form">
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="admin-form__form"
        noValidate
      >
        <div className="admin-form__form-content">
          <div className="admin-form__field">
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextInput
                  label="Product Name"
                  onChange={field.onChange}
                  value={field.value ?? ''}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required
                />
              )}
            />
          </div>

          <div className="admin-form__field">
            <Controller
              name="description"
              control={control}
              render={({ field, fieldState }) => (
                <TextInput
                  multiline
                  rows={4}
                  label="Description"
                  onChange={field.onChange}
                  value={field.value ?? ''}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required
                />
              )}
            />
          </div>

          <div className="admin-form__field-row admin-form__field-row--half">
            <div className="admin-form__field">
              <Controller
                name="price"
                control={control}
                render={({ field, fieldState }) => (
                  <TextInput
                    type="number"
                    label="Price"
                    onChange={(e) => {
                      const value =
                        e.target.value === '' ? 0 : parseFloat(e.target.value);
                      field.onChange(Number.isNaN(value) ? 0 : value);
                    }}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    required
                  />
                )}
              />
            </div>
          </div>

          {/*NEW PRICE */}
          <div className="admin-form__field-row admin-form__field-row--half">
            <div className="admin-form__field">
              <Controller
                name="newPrice"
                control={control}
                render={({ field, fieldState }) => (
                  <TextInput
                    type="number"
                    label="New Price"
                    onChange={(e) => {
                      const value =
                        e.target.value === '' ? 0 : parseFloat(e.target.value);
                      field.onChange(Number.isNaN(value) ? 0 : value);
                    }}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="admin-form__field">
            <Controller
              name="tags"
              control={control}
              render={({ field }) => (
                <Select
                  multiple
                  label="Tags"
                  value={field.value || []}
                  options={tagsList?.items?.map((tag) => ({
                    id: tag.id,
                    label: tag.name,
                    color: tag.color,
                    renderItem: (
                      <div className="admin-form__tags-badge">
                        <div
                          className="admin-form__tags-badge__color-box"
                          style={{ backgroundColor: tag.color || '#000' }}
                        />
                        <p className="caption">{tag.name}</p>
                      </div>
                    ),
                  }))}
                  onChange={field.onChange}
                  isClearable
                  error={!!errors.tags}
                  helperText={errors.tags?.message}
                />
              )}
            />
          </div>

          <div className="admin-form__images-section">
            <h5>Product Photos (up to 5)</h5>

            <Button variant="outlined" component="label">
              Upload Images
              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={handleImageUpload}
              />
            </Button>

            {images.length > 0 && (
              <div className="admin-form__images-preview">
                {images.map((img, i) => {
                  const preview =
                    typeof img === 'string' ? img : URL.createObjectURL(img);
                  return (
                    <div key={i} className="admin-form__image-card">
                      <CardMedia
                        component="img"
                        image={preview}
                        height="120"
                        sx={{ objectFit: 'cover' }}
                      />
                      <Button
                        size="small"
                        onClick={() => removeImage(i)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          background: 'rgba(0,0,0,0.4)',
                          color: '#fff',
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* CUSTOM FIELDS */}
          <div className="admin-form__custom-fields">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h5>Custom Fields</h5>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addCustomField}
              >
                Add Custom Field
              </Button>
            </div>

            {fields.map((field, index) => {
              const fieldType = watch(`customFields.${index}.type`);

              return (
                <div key={field.id} className="admin-form__custom-field">
                  <div className="admin-form__custom-field-header">
                    <h5>Custom Field {index + 1}</h5>
                    <IconButton
                      color="error"
                      onClick={() => remove(index)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </div>

                  <div className="admin-form__field-row admin-form__field-row--half">
                    <div className="admin-form__field">
                      <Controller
                        name={`customFields.${index}.name` as const}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextInput
                            label="Field Name"
                            onChange={field.onChange}
                            value={field.value ?? ''}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            required
                          />
                        )}
                      />
                    </div>

                    <div className="admin-form__field">
                      <Controller
                        name={`customFields.${index}.type` as const}
                        control={control}
                        render={({ field, fieldState }) => (
                          <Select
                            options={[
                              { id: 'string', label: 'String' },
                              { id: 'image', label: 'Image' },
                              { id: 'Checkbox', label: 'Checkbox' },
                              { id: 'Radio', label: 'Radio' },
                            ]}
                            label="Type"
                            value={field.value}
                            onChange={field.onChange}
                            required
                            error={!!fieldState.error}
                          />
                        )}
                      />
                    </div>
                  </div>
                  {fieldType !== 'Checkbox' && fieldType !== 'Radio' ? (
                    <>
                      <div className="admin-form__field-row admin-form__field-row--half">
                        <div className="admin-form__field">
                          <Controller
                            name={`customFields.${index}.placeholder` as const}
                            control={control}
                            render={({ field, fieldState }) => (
                              <TextInput
                                label="Placeholder"
                                onChange={field.onChange}
                                value={field.value ?? ''}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                              />
                            )}
                          />
                        </div>
                      </div>

                      <div className="admin-form__field-row admin-form__field-row--half">
                        <div className="admin-form__field">
                          <Controller
                            name={`customFields.${index}.minLength` as const}
                            control={control}
                            render={({ field, fieldState }) => (
                              <TextInput
                                type="number"
                                label="Min Length"
                                onChange={(e) => {
                                  const value =
                                    e.target.value === ''
                                      ? 0
                                      : parseInt(e.target.value, 10);
                                  field.onChange(
                                    Number.isNaN(value) ? 0 : value
                                  );
                                }}
                                value={field.value ?? 0}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                              />
                            )}
                          />
                        </div>
                        <div className="admin-form__field">
                          <Controller
                            name={`customFields.${index}.maxLength` as const}
                            control={control}
                            render={({ field, fieldState }) => (
                              <TextInput
                                type="number"
                                label="Max Length"
                                inputProps={{
                                  min: 0,
                                }}
                                onChange={(e) => {
                                  const value =
                                    e.target.value === ''
                                      ? 0
                                      : parseInt(e.target.value, 10);
                                  field.onChange(
                                    Number.isNaN(value) ? 0 : value
                                  );
                                }}
                                value={field.value ?? 0}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                              />
                            )}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <CustomFieldsOptions fieldName={`customFields.${index}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* SAVE BUTTON */}
        <div className="admin-form__actions">
          <Button variant="contained" type="submit" disabled={isLoading}>
            Save
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PhoneCaseForm;
